import React, { useState, useEffect, useCallback } from 'react';
import { countryEducationAPI } from '../services/api';
import { FiLayers, FiSearch, FiBookOpen } from 'react-icons/fi';

// Country Education Reference (document tracker follow-up, 2026-09-29) —
// staff/agent reference for what a country's diplomas/exams are called and
// how its school system maps onto Turkish grades, sourced from Türkiye's
// MEB Denklik Kılavuzu. Not a per-student checklist — see the
// "Equivalence Letter (Denklik)" group on the student's document checklist
// for that.
const CountryGuide = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async (term) => {
    setLoading(true);
    setError('');
    try {
      const res = await countryEducationAPI.list(term);
      const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
      setEntries(data);
      if (data.length && !data.some((e) => e.id === selectedId)) {
        setSelectedId(data[0].id);
      }
      if (!data.length) setSelectedId(null);
    } catch (err) {
      setError('Could not load the country reference.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => load(search), 300);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const selected = entries.find((e) => e.id === selectedId);

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 4px' }}>
        <FiLayers /> Country Guide
      </h2>
      <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 20px' }}>
        What a country's diplomas/exams are called and how its school system maps onto Turkish grades — sourced
        from Türkiye's MEB Denklik Kılavuzu (Equivalence Guide). For advising students and doing their equivalence
        (Denklik) process.
      </p>

      <div style={{ position: 'relative', maxWidth: 420, marginBottom: 16 }}>
        <FiSearch style={{ position: 'absolute', left: 12, top: 12, color: '#9ca3af' }} />
        <input
          type="text"
          placeholder="Search by country..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: '100%', padding: '10px 12px 10px 36px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
        />
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ width: 280, flexShrink: 0, border: '1px solid #e5e7eb', borderRadius: 10, maxHeight: 620, overflowY: 'auto' }}>
          {loading ? (
            <p style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>Loading…</p>
          ) : entries.length === 0 ? (
            <p style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>No countries match.</p>
          ) : (
            entries.map((e) => (
              <div
                key={e.id}
                onClick={() => setSelectedId(e.id)}
                style={{
                  padding: '10px 14px', cursor: 'pointer', fontSize: 13.5,
                  background: e.id === selectedId ? '#ECEFFE' : 'transparent',
                  color: e.id === selectedId ? '#001FF2' : '#111827',
                  fontWeight: e.id === selectedId ? 600 : 400,
                  borderBottom: '1px solid #f3f4f6',
                }}
              >
                {e.country_name_en}{e.sub_system_label ? ` — ${e.sub_system_label}` : ''}
              </div>
            ))
          )}
        </div>

        <div style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, minHeight: 300 }}>
          {!selected ? (
            <p style={{ color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiBookOpen /> Select a country to see its reference details.
            </p>
          ) : (
            <>
              <h3 style={{ margin: '0 0 4px', color: '#0B1760' }}>
                {selected.country_name_en}{selected.sub_system_label ? ` — ${selected.sub_system_label}` : ''}
              </h3>
              {selected.country_name_official && (
                <p style={{ color: '#9ca3af', fontSize: 12, margin: '0 0 16px' }}>
                  {selected.country_name_official} · {selected.guide_version}
                  {selected.source_page_start ? ` · guide p.${selected.source_page_start}${selected.source_page_end && selected.source_page_end !== selected.source_page_start ? `–${selected.source_page_end}` : ''}` : ''}
                </p>
              )}

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#8a94b8', letterSpacing: '0.04em', marginBottom: 4 }}>EDUCATION STRUCTURE</div>
                <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>{selected.education_structure_summary}</p>
              </div>

              {selected.certificate_names?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#8a94b8', letterSpacing: '0.04em', marginBottom: 4 }}>CERTIFICATE / DIPLOMA NAMES</div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                    {selected.certificate_names.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
              )}

              <div style={{ display: 'flex', gap: 24, marginBottom: 14, flexWrap: 'wrap' }}>
                {selected.exam_board && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#8a94b8', letterSpacing: '0.04em', marginBottom: 4 }}>EXAM BOARD</div>
                    <p style={{ fontSize: 14, margin: 0 }}>{selected.exam_board}</p>
                  </div>
                )}
                {selected.language_of_instruction && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#8a94b8', letterSpacing: '0.04em', marginBottom: 4 }}>LANGUAGE OF INSTRUCTION</div>
                    <p style={{ fontSize: 14, margin: 0 }}>{selected.language_of_instruction}</p>
                  </div>
                )}
              </div>

              {selected.special_notes && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', letterSpacing: '0.04em', marginBottom: 4 }}>SPECIAL NOTES</div>
                  <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0, color: '#92400e' }}>{selected.special_notes}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CountryGuide;
